import React from "react";
import { TicketPriorityTag, TicketStatusTag } from "../../SupportFields";
import { getStatusText, type SupportTicket } from "../../RequestData";
import type { TicketDetail } from "../../TicketDetail";

/** The ticket's Service IDs as a list, whatever shape the endpoint sent. */
export function toServiceIds(serviceId: unknown): string[] {
  if (Array.isArray(serviceId)) return serviceId.map(String);
  if (typeof serviceId !== "string") return [];
  return serviceId
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

type Props = {
  /** Ticket number as the list shows it (KS-484 in Kontrol Edge). */
  number?: string | number;
  /** Request type as the list shows it; the detail endpoint does not carry it. */
  type?: string;
  ticket: TicketDetail;
};

/**
 * Header of the ticket detail modal (CTX-1121): number and type on top, the
 * subject on its own, who opened it and when underneath, and status and
 * priority to the right. Passed to `GenericModal` as its `title`, which
 * accepts any node.
 */
/**
 * A long subject steps down in size instead of wrapping over three or four
 * lines at the full heading size, which left the header mostly empty space
 * next to the tags (Jose, CTX-1256).
 */
export function subjectSizeClass(subject: string): string {
  if (subject.length > 120) return "text-sm";
  if (subject.length > 60) return "text-base";
  return "text-lg";
}

export function TicketDetailsHeader({ number, type, ticket }: Props) {
  // The detail endpoints send the status as a string or as the list's object.
  const status = getStatusText(ticket.status as SupportTicket["status"]);
  const hasPriority = Number.isFinite(Number(ticket.priority));
  const opener = ticket.requester?.name || ticket.requester?.email;
  const openedAt = ticket.date?.friendly;
  const subject = ticket.subject || "Untitled ticket";
  const serviceIds = toServiceIds(ticket.service_id);

  return (
    <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        {(number || type) && (
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs font-normal">
            {number && (
              <span className="font-mono text-semantic-text-secondary">
                {number}
              </span>
            )}
            {type && (
              <span className="rounded px-2 py-0.5 font-semibold bg-semantic-bg-brand-secondary text-semantic-text-brand">
                {type}
              </span>
            )}
          </div>
        )}
        <span
          className={`block font-semibold text-semantic-text-primary break-words ${subjectSizeClass(subject)}`}
        >
          {subject}
        </span>
        {(opener || openedAt) && (
          <span className="mt-0.5 block text-xs font-normal text-semantic-text-secondary">
            {[openedAt && `Opened ${openedAt}`, opener && `by ${opener}`]
              .filter(Boolean)
              .join(" ")}
          </span>
        )}
        {/* On a phone the services sit up here, by the status; the Details
            they come from are folded (CTX-1433). */}
        {serviceIds.length > 0 && (
          <span className="mt-1 block font-mono text-xs font-normal text-semantic-text-secondary sm:hidden">
            {serviceIds.join(" · ")}
          </span>
        )}
      </div>
      <div className="flex flex-none items-center gap-2">
        {status !== "-" && <TicketStatusTag value={status} />}
        {hasPriority && <TicketPriorityTag value={Number(ticket.priority)} />}
      </div>
    </div>
  );
}
